import { Tooltip } from '@healingpaper/cell'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import EmailIcon from './svgs/email.svg'
import Point3000Icon from './svgs/point_3000.svg'
import Point5000Icon from './svgs/point_5000.svg'

import { SignInTargetType, SocialSignInButton } from '@/shared/domains/oauth'
import color from '@/components/cell/designTokens/color'
import typography from '@/components/cell/designTokens/typography'
import { generateTagId } from '@/utils/test-automation'
import FlexCol from '@/components/shared/FlexCol'
import { useLocales } from '@/shared/hooks/useLocales'
import { Locales } from '@/utils/language'
import RecaptchaFooter from '@/features/signin/components/recaptcha-footer'

export interface Props {
  onClickSignin: (target: string) => void
}

export const SocialSignInTargets: Record<Locales, SignInTargetType[]> = {
  [Locales.KO_KR]: ['kakao', 'apple', 'line'],
  [Locales.JA_JP]: ['line', 'apple', 'kakao'],
  [Locales.EN_US]: ['apple', 'kakao', 'line'],
  [Locales.TH_TH]: ['apple', 'kakao', 'line'],
  [Locales.ZH_HANS_CN]: ['apple', 'kakao', 'line'],
  [Locales.ZH_HANT_TW]: ['apple', 'kakao', 'line'],
}

export interface ToolTipContentProps {
  registerText: string
  receiveText: string
  language: string
}

const ToolTipContent = (props: ToolTipContentProps) => {
  const { i18n } = useTranslation()
  return (
    <FlexCol items="center" gap={4}>
      <span>{props.registerText}</span>
      {i18n.language === 'ko-KR' && <Point5000Icon />}
      {i18n.language === 'ja-JP' && <Point3000Icon />}
      <span>{props.receiveText}</span>
    </FlexCol>
  )
}

const SignInPageTemplate = ({ onClickSignin }: Props): JSX.Element => {
  const { t } = useTranslation()
  const { locale, isLocale } = useLocales()
  const router = useRouter()

  return (
    <FlexCol className="flex-1" justify="center">
      <StyledContainer>
        <StyledWelcomeText>{t('pages.signIn.welcome')}</StyledWelcomeText>
        <div className="mb-12 mt-[132px] w-full items-center flex flex-col">
          <Tooltip
            style={{
              position: 'absolute',
              width: '180px',
              height: 'fit-content',
              fontWeight: '700',
              color: color.gray800,
              boxSizing: 'border-box',
              textAlign: 'center',
              whiteSpace: 'break-spaces',
            }}
            type="description"
            content={
              <ToolTipContent
                registerText={t('pages.signIn.registerNow')}
                receiveText={t('pages.signIn.receive')}
                language={locale}
              />
            }
            showed={isLocale.JP || isLocale.KR}
            onClick={() => Object}
            position="top"
          >
            <></>
          </Tooltip>
          <div className="flex flex-col gap-2 w-full">
            {SocialSignInTargets[locale].map((target) => (
              <SocialSignInButton
                key={target}
                target={target}
                text={t(`pages.signIn.socialNetwork.${target}`)}
                onClick={() => {
                  onClickSignin(target)
                }}
              />
            ))}
          </div>

          <div>
            <StyledSeparator>{t('pages.signIn.or')}</StyledSeparator>
            <StyledIconButtonWrapper>
              <SocialSignInButton
                target="facebook"
                onlyIcon={true}
                text={t(`pages.signIn.socialNetwork.facebook`, '')}
                onClick={() => {
                  onClickSignin('facebook')
                }}
              />
              <StyledEmailButton
                id={generateTagId('login', 'section', 'btn', 'login', 'email')}
                onClick={() => {
                  onClickSignin('email')
                  const returnUrl = router.query.return_url as string
                  router.push(`/signin/email` + (returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : ''))
                }}
              >
                <EmailIcon />
              </StyledEmailButton>
            </StyledIconButtonWrapper>
          </div>
        </div>
        <RecaptchaFooter />
      </StyledContainer>
    </FlexCol>
  )
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px 49px;
  flex: 1;
`

const StyledWelcomeText = styled.h1`
  text-align: center;
  ${typography.title1}
  white-space: break-spaces;
`

const StyledSeparator = styled.div`
  ${({ theme }) => theme.typography.description2}
  color: ${({ theme }) => theme.colors.gray900};
  text-align: center;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 24px auto;
  width: fit-content;

  &::before,
  &::after {
    width: 90px;
    height: 1px;
    content: '';
    background-color: ${({ theme }) => theme.colors.gray400};
  }
`

const StyledIconButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
`

const StyledEmailButton = styled.button`
  width: 36px;
  height: 36px;
  padding: 0px;
  background-color: transparent;
  border: none;
  cursor: pointer;
`

export default SignInPageTemplate
